import clsx from 'clsx';
import React, { ReactNode } from 'react';

import './BayPillToggle.scss';

export interface PillToggleOption<T extends string = string> {
	/** The value emitted via onChange when this option is selected. */
	value: T;
	/** The visible label. */
	label: ReactNode;
	/** Optional content rendered after the label (e.g. a <BayBadge>). */
	badge?: ReactNode;
	/** Disable just this option. */
	disabled?: boolean;
}

export interface BayPillToggleProps<T extends string = string> {
	/** The available options. Supports two or more; lay out side by side in a pill track. */
	options: PillToggleOption<T>[];
	/** The currently selected value (controlled). */
	value: T;
	/** Called with the next value when an option is clicked. */
	onChange: (_value: T) => void;
	/** Visual size. Defaults to null. */
	size?: 'sm' | 'md' | null;
	/**
	 * Colour treatment. 'primary' is the blue track with a solid blue selected
	 * pill; 'neutral' is the quieter grey track with a white selected pill, for
	 * places where the toggle is chrome rather than the point of the screen.
	 */
	variant?: 'primary' | 'neutral';
	/**
	 * Corner treatment. 'pill' is the rounded default; 'square' drops the radius
	 * on both the track and its segments, for toggles that sit flush against
	 * squared-off panel chrome.
	 */
	shape?: 'pill' | 'square';
	/** Stretch the track to fill its container, distributing options evenly. */
	fullWidth?: boolean;
	/** Disable the whole control. */
	disabled?: boolean;
	/** Accessible group label (applied to the radiogroup). */
	ariaLabel?: string;
	className?: string;
	style?: React.CSSProperties;
}

/**
 * A pill-style segmented toggle for a small set of mutually-exclusive options
 * (e.g. Static / Dynamic, PostCards / Flyers). Controlled: pass `value` + `onChange`.
 *
 * Renders as an ARIA radiogroup so it's keyboard- and screen-reader-friendly.
 */
function BayPillToggle<T extends string = string>({
	options,
	value,
	onChange,
	size = null,
	variant = 'primary',
	shape = 'pill',
	fullWidth = false,
	disabled = false,
	ariaLabel,
	className,
	style,
}: BayPillToggleProps<T>) {
	const handleSelect = (option: PillToggleOption<T>) => {
		if (disabled || option.disabled || option.value === value) return;
		onChange(option.value);
	};

	return (
		<div
			role="radiogroup"
			aria-label={ariaLabel}
			className={clsx(
				'bay-pill-toggle',
				size ? `bay-pill-toggle--${size}` : null,
				variant === 'neutral' ? 'bay-pill-toggle--neutral' : null,
				shape === 'square' ? 'bay-pill-toggle--square' : null,
				{
					'bay-pill-toggle--full-width': fullWidth,
					'bay-pill-toggle--disabled': disabled,
				},
				className,
			)}
			style={style}
		>
			{options.map((option) => {
				const isActive = option.value === value;
				const isDisabled = disabled || option.disabled;
				return (
					<button
						key={option.value}
						type="button"
						role="radio"
						aria-checked={isActive}
						disabled={isDisabled}
						className={clsx('bay-pill-toggle__option', {
							'bay-pill-toggle__option--active': isActive,
						})}
						// Preserve any text selection / focus the consumer cares about (e.g. RTE).
						onMouseDown={(event) => event.preventDefault()}
						onClick={() => handleSelect(option)}
					>
						<span className="bay-pill-toggle__label">{option.label}</span>
						{option.badge != null && (
							<span className="bay-pill-toggle__badge">{option.badge}</span>
						)}
					</button>
				);
			})}
		</div>
	);
}

export default BayPillToggle;
